import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage } from '../../stores/coreSession';
import { drawTradeDialog } from './trade_dialog';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';
import { trackEvent } from '../../utils/analytics';

const FALLBACK_ASSETS = [
  { id: 'weed', symbol: 'WEED', name: 'WEED', sub: 'LEGACY COMMODITY' },
  { id: 'quaaludes', symbol: 'LUDES', name: 'LUDES', sub: 'LEGACY COMMODITY' },
  { id: 'speed', symbol: 'SPEED', name: 'SPEED', sub: 'LEGACY COMMODITY' },
  { id: 'cocaine', symbol: 'COCAINE', name: 'COCAINE', sub: 'LEGACY COMMODITY' },
];

const state = {
  assets: FALLBACK_ASSETS,
  selected: 0,
  status: 'NOT SYNCED',
  loading: false,
  loadedOnce: false,
  tradeOpen: false,
  side: 'BUY',
  qty: 1,
  quote: null,
  quoteError: null,
  receipt: null,
  error: null,
};

let loadPromise = null;
let quoteSeq = 0;

if (typeof window !== 'undefined') {
  window.addEventListener('dcw:escape', (event) => {
    if (state.tradeOpen) {
      state.tradeOpen = false;
      event.preventDefault();
    }
  });
}

function text(ctx, value, x, y, color = TERMINAL.TEXT, scale = 1) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, scale);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function shortName(value) {
  return String(value || '--').replace(/\s*\(.*\)$/, '').toUpperCase();
}

function assetFromContent(asset) {
  return {
    id: asset.assetId,
    symbol: String(asset.assetId || '').toUpperCase(),
    name: shortName(asset.streetName || asset.name || asset.assetId),
    sub: asset.market === 'public' ? 'PUBLIC MARKET' : String(asset.market || '').toUpperCase(),
  };
}

function owned(assetId) {
  const core = useCoreSession.getState().state;
  const slot = (core?.inventory || []).find((item) => item.assetId === assetId);
  return slot?.quantity ?? 0;
}

function selectedAsset() {
  return state.assets[state.selected] || state.assets[0];
}

function drawTop(ctx, safe) {
  const mobile = safe.width < 500;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, 24);
  rule(ctx, 0, 23, safe.width, TERMINAL.GREEN_DARK);
  text(ctx, 'DOPEOS // MARKET', 8, 4, TERMINAL.TEXT);
  if (!mobile) text(ctx, 'BRONX // 149TH', 216, 4, TERMINAL.TEXT_DIM);
  const core = useCoreSession.getState();
  const label = core.status === 'online' ? (state.loading ? 'SYNC' : 'MARKET OPEN') : 'OFFLINE';
  text(ctx, label, safe.width - 88, 4, core.status === 'online' ? TERMINAL.TEXT : TERMINAL.TEXT_DIM);
  if (!mobile) text(ctx, state.status, safe.width - 248, 4, TERMINAL.TEXT_DIM);
}

function drawAssets(ctx, x, y, w, h, mobile = false) {
  DopeUI.Panel(ctx, x, y, w, h, 'COMMODITIES // STREET PRICES');
  text(ctx, mobile ? '#  ASSET       BUY     SELL   OWN' : '#  ASSET          BUY      SELL     OWN  HEAT', x + 10, y + 24, TERMINAL.TEXT_DIM);
  rule(ctx, x + 8, y + 40, w - 16);
  const rowStep = mobile ? 44 : 38;
  const rowH = mobile ? 44 : 34;
  state.assets.forEach((asset, index) => {
    const rowY = y + 54 + index * rowStep;
    const selected = index === state.selected;
    if (selected) {
      ctx.fillStyle = TERMINAL.GREEN_DARK;
      ctx.fillRect(x + 6, rowY - 4, w - 12, rowH - 2);
    }
    const buy = asset.buy?.unitPrice?.amount ? `$${asset.buy.unitPrice.amount}` : '--';
    const sell = asset.sell?.unitPrice?.amount ? `$${asset.sell.unitPrice.amount}` : '--';
    const line = mobile
      ? `${selected ? '>' : ' '} ${index + 1} ${asset.name.slice(0, 10).padEnd(10)} ${buy.padStart(7)} ${sell.padStart(7)} ${String(owned(asset.id)).padStart(3)}`
      : `${selected ? '>' : ' '} ${index + 1} ${asset.name.padEnd(14).slice(0, 14)} ${buy.padStart(8)} ${sell.padStart(8)} ${String(owned(asset.id)).padStart(4)}  +${asset.buy?.heatDelta ?? 0}`;
    text(ctx, line, x + 10, rowY, selected ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
    text(ctx, mobile ? `   ${asset.sub} // HEAT +${asset.buy?.heatDelta ?? 0}` : `   ${asset.sub}`, x + 10, rowY + 17, TERMINAL.TEXT_DIM);
    interactiveRegistry.register({
      id: `market-asset-${asset.id}`,
      x: x + 6,
      y: rowY - 4,
      w: w - 12,
      h: rowH,
      onClick: () => {
        state.selected = index;
        state.quote = null;
        state.quoteError = null;
        refreshQuote();
      },
    });
  });
}

function drawOrder(ctx, x, y, w, h) {
  const asset = selectedAsset();
  DopeUI.Panel(ctx, x, y, w, h, 'ORDER // TICKET');
  text(ctx, asset.name, x + 10, y + 28, TERMINAL.TEXT_BRIGHT);
  text(ctx, asset.sub, x + 10, y + 44, TERMINAL.TEXT_DIM);
  text(ctx, 'SIDE', x + 10, y + 68, TERMINAL.TEXT_DIM);
  text(ctx, state.side, x + 150, y + 68, TERMINAL.TEXT);
  text(ctx, 'UNIT', x + 10, y + 86, TERMINAL.TEXT_DIM);
  text(ctx, state.quote?.unitPrice?.amount ? `$${state.quote.unitPrice.amount}` : '--', x + 150, y + 86, TERMINAL.TEXT);
  text(ctx, 'TOTAL', x + 10, y + 104, TERMINAL.TEXT_DIM);
  text(ctx, state.quote?.total?.amount ? `$${state.quote.total.amount}` : '--', x + 150, y + 104, TERMINAL.TEXT_BRIGHT);
  text(ctx, 'HEAT', x + 10, y + 122, TERMINAL.TEXT_DIM);
  text(ctx, state.quote ? `+${state.quote.heatDelta ?? 0}` : '--', x + 150, y + 122, TERMINAL.TEXT);
  text(ctx, 'CASH AFTER', x + 10, y + 140, TERMINAL.TEXT_DIM);
  text(ctx, state.quote?.cashAfter?.amount ? `$${state.quote.cashAfter.amount}` : '--', x + 150, y + 140, TERMINAL.TEXT);
  const reason = state.quoteError
    ? state.quoteError
    : state.quote?.disabledReasons?.[0]?.message
      ? state.quote.disabledReasons[0].message.toUpperCase()
      : state.quote?.canExecute
        ? 'PRICE LOCKED'
        : 'AWAITING PRICE';
  text(ctx, reason.slice(0, 28), x + 10, y + 160, state.quoteError ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);

  const buttonH = w > 300 ? 44 : 26;
  const buttonGap = 8;
  const bottomY = y + h - 12;
  const backY = bottomY - buttonH;
  const tradeY = backY - buttonH - buttonGap;
  DopeUI.Button(ctx, x + 10, tradeY, w - 20, buttonH, 'TRADE', state.quote?.canExecute ? 'hover' : 'normal');
  DopeUI.Button(ctx, x + 10, backY, w - 20, buttonH, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'market-trade',
    x: x + 10,
    y: tradeY,
    w: w - 20,
    h: buttonH,
    onClick: () => {
      trackEvent('trade_start', { assetId: asset.id, side: state.side.toLowerCase(), source: 'market_view' });
      state.tradeOpen = true;
      state.error = null;
      state.receipt = null;
      refreshQuote();
    },
  });
  interactiveRegistry.register({
    id: 'market-back',
    x: x + 10,
    y: backY,
    w: w - 20,
    h: buttonH,
    onClick: () => navigateTo('main_menu'),
  });
}

async function refreshQuote() {
  const asset = selectedAsset();
  if (!asset) return;
  const serial = ++quoteSeq;
  state.quoteError = null;
  try {
    const quote = await useCoreSession.getState().quoteTrade(asset.id, state.side.toLowerCase(), state.qty);
    if (serial !== quoteSeq) return;
    state.quote = quote;
  } catch (error) {
    if (serial !== quoteSeq) return;
    state.quote = null;
    state.quoteError = coreErrorMessage(error);
  }
}

async function loadMarket(force = false) {
  if (loadPromise) return loadPromise;
  if (state.loadedOnce && !force) return null;
  state.loadedOnce = true;
  loadPromise = (async () => {
    state.loading = true;
    state.error = null;
    try {
      const content = await useCoreSession.getState().loadContent();
      const assets = (content?.assets || [])
        .filter((asset) => asset.market === 'public' && asset.tradable !== false)
        .map(assetFromContent);
      state.assets = assets.length ? assets : FALLBACK_ASSETS;
      const quoted = await Promise.all(
        state.assets.map(async (asset) => {
          const [buy, sell] = await Promise.all([
            useCoreSession.getState().quoteTrade(asset.id, 'buy', 1),
            useCoreSession.getState().quoteTrade(asset.id, 'sell', 1),
          ]);
          return { ...asset, buy, sell };
        }),
      );
      state.assets = quoted;
      state.status = 'QUOTES';
      await refreshQuote();
    } catch (error) {
      state.status = 'OFFLINE';
      state.error = coreErrorMessage(error);
      state.quote = null;
    } finally {
      state.loading = false;
      loadPromise = null;
    }
  })();
  return loadPromise;
}

async function executeTrade() {
  const asset = selectedAsset();
  if (!asset || !state.quote?.canExecute) return;
  state.loading = true;
  state.error = null;
  try {
    const result = await useCoreSession.getState().executeTrade(asset.id, state.side.toLowerCase(), state.qty);
    state.receipt = { detail: `${result.trade?.total?.amount ?? '--'} DCW_USD // HEAT ${result.trade?.heatDelta ?? 0}` };
    state.tradeOpen = false;
    state.quote = null;
    await loadMarket(true);
  } catch (error) {
    state.error = coreErrorMessage(error);
  } finally {
    state.loading = false;
  }
}

registerView('market', function renderMarket(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  if (!state.loadedOnce) loadMarket();
  drawTop(ctx, safe);
  const mobile = safe.width < 500;
  if (mobile) {
    drawAssets(ctx, 8, 32, safe.width - 16, 180, true);
    drawOrder(ctx, 8, 216, safe.width - 16, 288);
    rule(ctx, 0, 504, safe.width);
    text(ctx, 'TAB FOCUS   ENTER SELECT', 8, 514, TERMINAL.TEXT_DIM);
    text(ctx, state.error || 'PRICES SHIFT AS YOU MOVE', 8, 530, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.GREEN);
  } else {
    drawAssets(ctx, 8, 40, 376, 264);
    drawOrder(ctx, 400, 40, 232, 264);
    rule(ctx, 0, 320, safe.width);
    text(ctx, 'TAB FOCUS   ENTER SELECT   BACK TO DESKTOP', 8, 330, TERMINAL.TEXT_DIM);
    text(ctx, state.error || 'PRICES SHIFT AS YOU MOVE', 8, 356, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.GREEN);
  }

  if (state.tradeOpen) {
    interactiveRegistry.clear();
    const asset = selectedAsset();
    const hits = drawTradeDialog(ctx, safe, asset, state, state.quote, {
      side: (side) => {
        state.side = side;
        state.quote = null;
        refreshQuote();
      },
      dec: () => {
        state.qty = Math.max(1, state.qty - 1);
        state.quote = null;
        refreshQuote();
      },
      inc: () => {
        state.qty = Math.min(20, state.qty + 1);
        state.quote = null;
        refreshQuote();
      },
      cancel: () => { state.tradeOpen = false; },
      execute: executeTrade,
    });
    hits.forEach((item) => interactiveRegistry.register(item));
  }
});

export default 'market';
